import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../../store/store';
import '../../styles/ai-assistant.styles.scss';
import { openAiTools, updateAiAssistantContent } from '../../../../../store/action-creators/ai-tools';
import { DATA_QA } from '../../helpers/data-qa';
import { useTranslation } from 'react-i18next';
import { isArticleEdit, resolveContentValue } from '../../helpers/ai-assistant.helper';
import { MainTitleContainer } from '../subComponents/main-title-container';
import { updateTempArticle, updateTempEditArticle } from '../../../../../store/action-creators/ArticleTempActionCreator';
import Article from '../../../../../models/article/Article';
import { initialAiToolsState } from '../../models/ai-assistant.models';
import { AiAssistantConfirmationModal } from '../subComponents/modal/ai-assistant-confirmation-modal';
import { SummaryContainer } from '../subComponents/summary-container';
import { SeoTitleContainer } from '../subComponents/seo-title-container';
import Seo from '../../../../../models/seo/Seo';

export const AiAssistantPanel = () => {
	const [t] = useTranslation();
	const dispatch = useDispatch();

	const state = useSelector((state: AppState) => state);
	const aiAssistant = state.aiTools;
	const aiAssistantContent = state.aiTools && state.aiTools.aiAssistantContent;
	const currentArticle =
		isArticleEdit() && state.tempArticle && state.tempArticle.articleEdit ? state.tempArticle.articleEdit : state.tempArticle.article;

	const modalRef = useRef<HTMLDivElement | null>(null);
	const [visible, setVisible] = useState(false);
	const [toggleInfoPanel, setToggleInfoPanel] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [hasScrolled, setHasScrolled] = useState(false);
	const bodyRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (aiAssistant && aiAssistant.aiToolsOpen) {
			setVisible(true);
		}
	}, [aiAssistant]);

	useEffect(() => {
		return () => {
			dispatch(openAiTools(false));
			dispatch(updateAiAssistantContent(initialAiToolsState.aiAssistantContent));
		};
	}, []);

	const closePanel = () => {
		setVisible(false);
		setTimeout(function () {
			dispatch(openAiTools(false));
		}, 250);
	};

	const onInfoToggle = () => setToggleInfoPanel(!toggleInfoPanel);

	const onSaveAndInsert = () => setShowModal(true);

	const saveAndInsert = () => {
		if (!aiAssistantContent) {
			return;
		}

		const mainTitleValue = resolveContentValue(aiAssistantContent.main_title, currentArticle.title);
		const summaryValue = resolveContentValue(aiAssistantContent.summary, currentArticle.subtitle);
		const seoTitleValue = resolveContentValue(aiAssistantContent.seo_title, currentArticle.seo ? currentArticle.seo.title : '');

		const seoData = Seo.builder(currentArticle.seo).withTitle(seoTitleValue).build();

		const articleData = Article.builder(currentArticle).withTitle(mainTitleValue).withSubtitle(summaryValue).withSeo(seoData).build();

		dispatch(isArticleEdit() ? updateTempEditArticle(articleData) : updateTempArticle(articleData));
		setShowModal(false);
		closePanel();
	};

	useEffect(() => {
		if (visible) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}

		return () => {
			document.body.style.overflow = '';
		};
	}, [visible]);

	useEffect(() => {
		const onScroll = () => {
			if (!bodyRef.current) return;
			setHasScrolled(bodyRef.current.scrollTop > 0);
		};

		const currentRef = bodyRef.current;
		if (currentRef) currentRef.addEventListener('scroll', onScroll);

		return () => {
			if (currentRef) currentRef.removeEventListener('scroll', onScroll);
		};
	}, []);

	const isSaveDisabled = useMemo(() => {
		if (!aiAssistantContent) return true;

		const mainTitle = aiAssistantContent.main_title && aiAssistantContent.main_title.current_content;
		const summary = aiAssistantContent.summary && aiAssistantContent.summary.current_content;
		const seoTitle = aiAssistantContent.seo_title && aiAssistantContent.seo_title.current_content;

		return !mainTitle && !summary && !seoTitle;
	}, [aiAssistantContent]);

	if (aiAssistant && !aiAssistant.aiToolsOpen && !visible) {
		return null;
	}

	return (
		<div ref={modalRef} data-qa={DATA_QA.AI_TOOLS_PANEL} className={'ai-tools-modal-container ' + (visible ? 'open' : '')}>
			<div className={`ai-tools-modal-header${hasScrolled ? ' scrolled' : ''}`}>
				<div className='ai-tools-modal-header-title'>
					<span className='ai-tools-title-label'>{t('ai_assistant')}</span>
					<div className='ai-assistant-info-icon' onClick={onInfoToggle} />
					{toggleInfoPanel && (
						<div className='ai-assistant-info-message'>
							<div className='ai-assistant-info-message-title'>
								{t('ai_assistant')}
								<div className='ai-assistant-info-close-icon' onClick={onInfoToggle} />
							</div>
							<div className='ai-assistant-info-message-description'>{t('ai_assistant_general_info_description')}</div>
						</div>
					)}
				</div>
				<div className='ai-tools-modal-header-buttons-group'>
					<div data-qa={DATA_QA.AI_TOOLS_PANEL_CLOSE_BTN} className='ai-assistant-close-btn' onClick={closePanel}>
						<span className='ai-assistant-close-btn-text'>{t('close')}</span>
					</div>
					<div
						data-qa={DATA_QA.AI_TOOLS_PANEL_SAVE_AND_INSERT_BTN}
						className={`ai-assistant-save-btn ${isSaveDisabled ? 'disabled' : ''}`}
						onClick={isSaveDisabled ? undefined : onSaveAndInsert}
					>
						<span className='ai-assistant-save-btn-text'>{t('insert')}</span>
					</div>
				</div>
			</div>
			<div ref={bodyRef} className='ai-tools-modal-body'>
				<MainTitleContainer article={currentArticle} aiAssistantContent={aiAssistantContent} />
				<SeoTitleContainer article={currentArticle} aiAssistantContent={aiAssistantContent} />
				<SummaryContainer article={currentArticle} aiAssistantContent={aiAssistantContent} />
			</div>
			<AiAssistantConfirmationModal setShowModal={setShowModal} showModal={showModal} saveAndInsert={saveAndInsert} />
		</div>
	);
};
